import type { ConversationRun, LiveSegment } from '@axilon-apps/domains-ontology-ui-state'
import { extractAssistantTurnTexts } from '../ToolTrace/MessageList'

/**
 * Mirror of `DISPATCH_SUBAGENT_TOOL_NAME` in
 * `packages/ontology/agents/src/agent-orchestrator/tools.ts`. The UI has no
 * dependency on the agents package, so the planner's tool name is duplicated
 * here. If the planner's tool protocol changes, update this constant — the
 * spec guards the behaviour.
 */
const DISPATCH_SUBAGENT_TOOL_NAME = 'dispatch_subagent'

/**
 * Rebuild a subagent's committed progress notes from its persisted messages,
 * keyed by the child run's plan-step position. Refresh-safe source for the
 * transcript's `liveSegmentsByStep`; within a live session the in-memory
 * carry on `ConversationRun.progressByStep` is preferred (see {@link pastTurnCot}).
 */
export function deriveSubagentSegments(children: ConversationRun[]): Record<number, LiveSegment[]> {
  const out: Record<number, LiveSegment[]> = {}
  for (const child of children) {
    const segments = extractAssistantTurnTexts(child.messages).map(
      (turn): LiveSegment => ({ kind: 'progress', text: turn.text })
    )
    if (segments.length > 0) out[child.position] = segments
  }
  return out
}

/**
 * Rebuild the orchestrator's inter-step narration from its persisted messages:
 * the text preceding each `dispatch_subagent` call is that step's intro. Text
 * before other planner tools is preamble (dropped live) or already rendered
 * from `raw_response` / `step_results`, so only dispatch intros are kept.
 */
export function deriveInterStepText(orchestratorRun: ConversationRun): Record<number, string> {
  const out: Record<number, string> = {}
  for (const turn of extractAssistantTurnTexts(orchestratorRun.messages)) {
    if (turn.toolName !== DISPATCH_SUBAGENT_TOOL_NAME) continue
    if (typeof turn.toolInput !== 'object' || turn.toolInput === null) continue
    const stepIndex = (turn.toolInput as { step_index?: unknown }).step_index
    if (typeof stepIndex === 'number' && Number.isInteger(stepIndex)) {
      out[stepIndex] = turn.text
    }
  }
  return out
}

/**
 * Chain-of-thought for a past turn's transcript. Merges the persisted-message
 * derivation (refresh-safe) with the in-memory carry-forward, with in-memory
 * winning per step — it's the freshest within the session, and the derivation
 * fills in after a page refresh once messages reload.
 */
export function pastTurnCot(turn: ConversationRun): {
  liveSegmentsByStep: Record<number, LiveSegment[]>
  interStepTextByStep: Record<number, string>
} {
  return {
    liveSegmentsByStep: {
      ...deriveSubagentSegments(turn.children),
      ...(turn.progressByStep ?? {})
    },
    interStepTextByStep: { ...deriveInterStepText(turn), ...(turn.interStepTextByStep ?? {}) }
  }
}
